import React, { useState, useEffect } from "react";
import Footer from "../Layout/Footer";
import Header from "../Layout/Header";
import Pagination from "../Layout/Pagination";
import { defaultPage } from "../../Common/Constant";

import {
  reconciliationList,
  getSingleReconciliationDownlodReport,
} from "../../Api/reconciliation";
import { forceDownloadFile } from "../../ApiService";

const Reconciliation = () => {
  const [mfIdentifier, setMfIdentifier] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [pageInfo, setPageInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeMfIdentifier, setActiveMfIdentifier] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");

  useEffect(() => {
    document.title = "Reconciliation - Mindforce";
  }, []);

  const fetchReconciliationData = async ({
    identifier = activeMfIdentifier,
    term = activeSearchTerm,
    pageNo = pageInfo.current_page || 1,
  } = {}) => {
    if (!String(identifier || "").trim()) {
      setHasSearched(false);
      setData([]);
      setPageInfo({});
      return;
    }

    setLoading(true);
    setHasSearched(true);
    try {
      const response = await reconciliationList({
        mfIdentifier: identifier,
        page: pageNo,
        recPerPage: defaultPage.perPage,
        searchTerm: term,
      });

      if (response.status === 1) {
        setData(response.payload || []);
        setPageInfo(response.paginationInfo || {});
      } else {
        setData([]);
        setPageInfo({});
      }
    } catch (error) {
      console.error("Error fetching reconciliation data:", error);
      setData([]);
      setPageInfo({});
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!String(mfIdentifier || "").trim()) {
      setHasSearched(false);
      setData([]);
      setPageInfo({});
      setActiveMfIdentifier("");
      setActiveSearchTerm("");
      return;
    }

    setPage(1);
    setActiveMfIdentifier(mfIdentifier);
    setActiveSearchTerm(searchTerm);
    fetchReconciliationData({
      identifier: mfIdentifier,
      term: searchTerm,
      pageNo: 1,
    });
  };

  const handleClear = () => {
    setMfIdentifier("");
    setSearchTerm("");
    setPage(1);
    setPerPage(10);
    setData([]);
    setPageInfo({});
    setHasSearched(false);
    setLoading(false);
    setActiveMfIdentifier("");
    setActiveSearchTerm("");
  };

  const handleDownloadReport = async () => {
    try {
      const response = await getSingleReconciliationDownlodReport({
        mfIdentifier: activeMfIdentifier || mfIdentifier,
      });

      if (response.payload && response.payload.reportURL) {
        await forceDownloadFile(response.payload.reportURL);
      } else {
        console.error("No URL returned from API");
      }
    } catch (error) {
      console.error("Error downloading reconciliation report:", error);
    }
  };

  const changePage = (p) => {
    setPage(p);
    pageInfo.current_page = p;
    fetchReconciliationData({
      identifier: activeMfIdentifier,
      term: activeSearchTerm,
      pageNo: p,
    });
  };

  const getEmptyMessage = () => {
    if (loading) return "Loading...";
    if (!hasSearched) return "Please enter identifier(s) and click Search";
    return "No data found";
  };

  return (
    <div>
      <Header />
      <div id="layout-wrapper">
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <div>
                      <div className="page-title-right">
                        <ol className="breadcrumb m-0">
                          <li className="breadcrumb-item">
                            <a>Support</a>
                          </li>
                          <li className="breadcrumb-item active">
                            Reconciliation
                          </li>
                        </ol>
                      </div>
                      <h4 className="mb-sm-0">Reconciliation</h4>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-xl-12 col-lg-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="report">
                        <form onSubmit={handleSearch}>
                          <div className="row">
                            <div className="col-md-6 col-lg-6">
                              <div className="mb-3">
                                <label
                                  htmlFor="mfIdentifier"
                                  className="form-label mb-0"
                                >
                                  Search Identifiers
                                </label>
                                <textarea
                                  className="form-control"
                                  id="mfIdentifier"
                                  rows="5"
                                  placeholder={
                                    "Paste one or more identifiers (Excel paste supported)\nOne per line, or separated by comma / tab"
                                  }
                                  value={mfIdentifier}
                                  onChange={(e) =>
                                    setMfIdentifier(e.target.value)
                                  }
                                ></textarea>
                                <small className="text-muted">
                                  Supports multi-line paste from Excel. Also
                                  accepts comma or tab separated values.
                                </small>
                              </div>
                            </div>

                            <div className="col-md-6 col-lg-6">
                              <div className="mb-3">
                                <div className="mt-8_rec">
                                  <button
                                    type="submit"
                                    className="btn btn-primary mx-2"
                                    disabled={loading}
                                  >
                                    {loading ? "Searching..." : "Search"}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger mx-2"
                                    onClick={handleClear}
                                    disabled={loading}
                                  >
                                    Clear
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-primary mx-2"
                                    disabled
                                  >
                                    Reconcile
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-primary mx-2"
                                    disabled
                                  >
                                    Complete
                                  </button>
                                  <button
                                    type="button"
                                    className={`btn btn-primary mx-2 ${
                                      data.length === 0 ? "disabled" : ""
                                    }`}
                                    onClick={handleDownloadReport}
                                    disabled={data.length === 0 || loading}
                                  >
                                    <i className="ri-folder-download-fill"></i>{" "}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="row justify-content-end mt-3">
                            <div className="col-md-4 col-lg-4">
                              <div className="text-end">
                                <div className="search">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Filter results..."
                                    autoComplete="off"
                                    id="search-options"
                                    value={searchTerm}
                                    disabled={!hasSearched || loading}
                                    onChange={(e) =>
                                      setSearchTerm(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleSearch(e);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="table-responsive position-relative">
                            {loading && (
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  background: "rgba(255,255,255,0.7)",
                                  zIndex: 2,
                                  minHeight: "160px",
                                }}
                              >
                                <div className="text-center">
                                  <div
                                    className="spinner-border text-primary"
                                    role="status"
                                  >
                                    <span className="visually-hidden">
                                      Loading...
                                    </span>
                                  </div>
                                  <div className="mt-2 text-muted">
                                    Searching identifiers...
                                  </div>
                                </div>
                              </div>
                            )}
                            <table className="table table-striped table-hover table_client mt-3">
                              <thead>
                                <tr
                                  className="bg-primary-1"
                                  style={{ color: "white" }}
                                >
                                  <th scope="col">Sr.No.</th>
                                  <th scope="col">Action</th>
                                  <th scope="col">ProjectCode</th>
                                  <th scope="col">ProjectName</th>
                                  <th scope="col">Supplier</th>
                                  <th scope="col">SupplierIdentifier</th>
                                  <th scope="col">UserIdentifier</th>
                                  <th scope="col">Status</th>
                                  <th scope="col">Date</th>
                                  <th scope="col">Geo.Location</th>
                                  <th scope="col">DeviceType</th>
                                  <th scope="col">BrowserDetail</th>
                                  <th scope="col">IsTestLink</th>
                                </tr>
                              </thead>
                              <tbody>
                                {!loading && data.length > 0 ? (
                                  data.map((item, index) => (
                                    <tr key={index}>
                                      <th scope="row">
                                        {(page - 1) * perPage + index + 1}
                                      </th>
                                      <td></td>
                                      <td>{item.projectCode}</td>
                                      <td>{item.projectName}</td>
                                      <td>{item.supplier}</td>
                                      <td>{item.supplierIdentifier}</td>
                                      <td>{item.userIdentifier}</td>
                                      <td>{item.status}</td>
                                      <td>{item.date}</td>
                                      <td>{item.geoLocation}</td>
                                      <td>{item.deviceType}</td>
                                      <td>{item.browserDetails}</td>
                                      <td>
                                        <div className="form-check ">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`formCheck-${index}`}
                                            checked={!!item?.testLink}
                                            readOnly
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td
                                      colSpan="13"
                                      className="text-center py-4 text-muted"
                                    >
                                      {getEmptyMessage()}
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          {hasSearched && data.length > 0 && (
                            <div className="pagination-wrapper">
                              <Pagination
                                pageInfo={pageInfo}
                                changePage={changePage}
                              />
                            </div>
                          )}
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Reconciliation;
